// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The six prose pages, from one component (S4-01 to S4-06).
//
// Privacy, accessibility, terms, support, how to play and about are the same page with different
// words, and the words are in `src/seo/pageCopy.ts` because the **prerenderer writes them too**: a
// crawler that never runs JavaScript receives the whole of this page's text, and a visitor receives
// the same text from React. One source, two consumers, no chance of the two disagreeing — which is
// the difference between a static page and a page that quietly says something else to a robot.
//
// **The route is passed in rather than read from the router.** `main.tsx` names the path when it
// mounts the page, so this component has no dependency on where it is mounted and can be rendered
// in a test with a path and nothing else.
//
// These pages are English only, which is recorded in `pageCopy.ts` and in report 01 §C4 rather than
// left as a surprise: the interface is fully bilingual and this prose is not.
//
// **`CopyDocument` is the document without the policy date** (S3-10). A machine guide is a document
// in exactly this shape — the prerenderer writes it through the same prose path — and it is not a
// policy, so "Last updated" under it would be a date nobody set. The markup is shared rather than
// copied, because the prerenderer mirrors it and two React copies would be two things to mirror.

import { Link } from 'react-router'
import { PAGE_COPY, POLICY_UPDATED, type PageCopy } from '../../seo/pageCopy'

/** One document's heading, lede, primary action, sections and questions. */
export function CopyDocument({ copy }: { readonly copy: PageCopy }): React.JSX.Element {
  return (
    <>
      <h1>{copy.h1}</h1>
      <p className="page__lede">{copy.intro}</p>
      {copy.action === undefined ? null : (
        <p>
          <Link className="button button--primary" to={copy.action.to} title={copy.action.text}>
            {copy.action.text}
          </Link>
        </p>
      )}
      {(copy.sections ?? []).map((section) => (
        <section className="copy__section" key={section.heading}>
          <h2>{section.heading}</h2>
          {(section.paragraphs ?? []).map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          {section.bullets === undefined ? null : (
            <ul className="copy__list">
              {section.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          )}
          {(section.code ?? []).map((block) => (
            <pre className="copy__code" key={block}>
              <code>{block}</code>
            </pre>
          ))}
          {section.link === undefined ? null : (
            <p>
              <Link to={section.link.to} title={section.link.text}>
                {section.link.text}
              </Link>
            </p>
          )}
        </section>
      ))}
      {copy.faq === undefined ? null : (
        <section className="copy__section">
          <h2>Common questions</h2>
          {/*
            A definition list, because that is what a question and its answer are — and because the
            `FAQPage` markup the prerenderer writes describes exactly these pairs. Marked up text
            that is not on the page is the failure report 01 §C5 names.
          */}
          <dl className="copy__faq">
            {copy.faq.map((entry) => (
              <div key={entry.question}>
                <dt>{entry.question}</dt>
                <dd>{entry.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </>
  )
}

export function CopyPage({ path }: { readonly path: string }): React.JSX.Element {
  const copy = PAGE_COPY[path]
  if (copy === undefined) {
    // Unreachable through the router — `main.tsx` mounts these by the same key this reads, and a
    // test holds the two lists together. A thrown error here would take the whole shell down for a
    // missing paragraph, so it renders the honest minimum instead.
    return (
      <div className="page">
        <h1>Nothing here</h1>
      </div>
    )
  }

  return (
    <div className="page copy">
      <CopyDocument copy={copy} />
      <p className="page__detail">Last updated {POLICY_UPDATED}.</p>
    </div>
  )
}
